import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Truck } from 'lucide-react';
import { useEffect } from 'react';

const AboutPage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div data-testid="about-page" className="pt-20 min-h-screen bg-void">
      {/* Hero Section - Meet Angel */}
      <section id="meet-angel" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 border-b border-white/5">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-4 block">
              About the Artist
            </span>
            <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter uppercase text-white mb-6">
              Meet<br />
              <span className="text-cyan-400">Angel</span>
            </h1>
            <p className="text-lg text-neutral-300 leading-relaxed mb-6">
              I am a service-disabled Marine Corps veteran, and my time in the military 
              ended in 2009. One of the ways I transitioned back into civilian life was 
              by attending trade school. I've always had a passion for art, so I decided 
              to pursue certification in automotive painting.
            </p>
            <p className="text-neutral-400 leading-relaxed mb-6">
              Over the years, I've worn many hats—working in painting, machining, 
              remodeling and management, small business owner. These days, I'm the local Uber driver in 
              Aurora, Illinois.
            </p>
            <p className="text-neutral-400 leading-relaxed mb-8">
              Only Skullz started out as a hobby and has grown into something I never 
              imagined a year ago. Even though I'm still working, this is something I 
              truly look forward to every day.
            </p>
            <Link
              to="/shop"
              data-testid="explore-collection-btn"
              className="inline-flex items-center gap-2 bg-cyan-400 text-black px-8 py-4 font-mono uppercase tracking-widest text-sm hover:bg-cyan-300 transition-all duration-300 hover:shadow-[0_0_20px_rgba(34,211,238,0.4)]"
            >
              Explore Collection <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="relative">
            <img
              src="https://customer-assets.emergentagent.com/job_141950ea-d755-47d0-b462-4d0e792f15d3/artifacts/c0awmo2r_unnamed.png"
              alt="Angel - Only Skullz Artist"
              className="w-full aspect-square object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-void via-transparent to-transparent" />
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-void-paper">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-2 block">
              The Process
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-white">
              From Concept to Creation
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 border border-white/10 bg-void/50">
              <span className="text-6xl font-heading font-black text-cyan-400/20 block mb-4">
                01
              </span>
              <h3 className="font-heading text-xl font-semibold text-white mb-4">
                Design & Planning
              </h3>
              <p className="text-neutral-400">
                Every piece begins with a vision. Whether I'm working with resin, metal, or wood, each material informs the design, and every step is carefully considered.
              </p>
            </div>

            <div className="p-8 border border-white/10 bg-void/50">
              <span className="text-6xl font-heading font-black text-cyan-400/20 block mb-4">
                02
              </span>
              <h3 className="font-heading text-xl font-semibold text-white mb-4">
                Layered Creation
              </h3>
              <p className="text-neutral-400">
                Each creation has its own distinct layers. Though the process can take hours, or an entire day, to cure, the depth and clarity that emerge breathe new life into the piece.
              </p>
            </div>

            <div className="p-8 border border-white/10 bg-void/50">
              <span className="text-6xl font-heading font-black text-cyan-400/20 block mb-4">
                03
              </span>
              <h3 className="font-heading text-xl font-semibold text-white mb-4">
                Finishing Touches
              </h3>
              <p className="text-neutral-400">
                Not all pieces make it to the final stage, but those that do are refined through polishing, sealing, or a protective coating that enhances their colors. Ultimately, this final step preserves the work and ensures its longevity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h3 className="font-heading text-2xl font-bold text-white mb-4">
                Quality Materials
              </h3>
              <p className="text-neutral-400">
                I've tried working with cheap materials—it doesn't work. Every material I use is selected intentionally and for a purpose.
              </p>
            </div>
            <div>
              <h3 className="font-heading text-2xl font-bold text-white mb-4">
                Unique Pieces
              </h3>
              <p className="text-neutral-400">
                No two pieces are the same. Everything I create is made individually by hand. Each piece must meet my standards, or it is placed in the donation section.
              </p>
            </div>
            <div>
              <h3 className="font-heading text-2xl font-bold text-white mb-4">
                Veteran Owned
              </h3>
              <p className="text-neutral-400">
                As a Marine Corps veteran, I bring discipline, attention to detail, 
                and dedication to every piece I create. Supporting Only Skullz means 
                supporting a veteran-owned small business.
              </p>
            </div>
            <div>
              <h3 className="font-heading text-2xl font-bold text-white mb-4">
                Custom Orders
              </h3>
              <p className="text-neutral-400">
                Have a specific vision in mind? I welcome custom commissions. 
                Let's collaborate to create a piece that's perfectly suited 
                to your space and taste.
              </p>
            </div>
          </div>

          {/* American-Made Section - Centered Below Veteran Owned & Custom Orders */}
          <div className="mt-16 pt-16 border-t border-white/10">
            <div className="max-w-3xl mx-auto text-center">
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-6">
                American-Made — Whenever Humanly Possible
              </h3>
              <div className="text-neutral-400 space-y-4 text-sm md:text-base leading-relaxed">
                <p>
                  As a United States Marine Corps veteran–owned company, commitment and accountability aren't marketing buzzwords for us—they're a way of life.
                </p>
                <p>
                  We actively source U.S.-made materials and work with American suppliers whenever humanly possible because it supports local jobs, improves quality control, and shortens supply chains. When we make sourcing decisions, we prioritize reliability, transparency, and partners who take pride in their work—just like we do.
                </p>
                <p>
                  While not every material is manufactured domestically today, we go out of our way to choose American-made options first and remain honest about where exceptions exist. Doing things the right way often takes more effort—but it's effort we believe is worth it.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-16 text-center">
            <Link
              to="/contact"
              data-testid="contact-link"
              className="inline-flex items-center gap-2 border border-white/20 text-white px-8 py-4 font-mono uppercase tracking-widest text-sm hover:bg-white hover:text-black transition-all duration-300"
            >
              Get in Touch <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Meet the Team Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-void-paper">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-2 block">
              The Crew
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-white">
              Meet the Team
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Eileen */}
            <div className="text-center">
              <div className="relative mb-6 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_141950ea-d755-47d0-b462-4d0e792f15d3/artifacts/e2iqew6g_Us%20at%20the%20Cubs%20Game.jpeg"
                  alt="Eileen - The Wife"
                  className="w-full aspect-square object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-void via-transparent to-transparent" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-2">
                Eileen
              </h3>
              <p className="text-cyan-400 font-mono text-sm uppercase tracking-widest mb-2">
                "The Wife"
              </p>
              <p className="text-neutral-400 text-sm">
                Master with ChatGPT
              </p>
            </div>

            {/* Ethel */}
            <div className="text-center">
              <div className="relative mb-6 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_789e9443-283a-429d-99a1-2af8cb011e09/artifacts/e2fv420o_Ethel.jpeg"
                  alt="Ethel - Advertisement and Marketing"
                  className="w-full aspect-square object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-void via-transparent to-transparent" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-2">
                Ethel
              </h3>
              <p className="text-cyan-400 font-mono text-sm uppercase tracking-widest mb-2">
                Advertisement & Marketing
              </p>
              <p className="text-neutral-400 text-sm">
                Light up the night, Haunt the streets
              </p>
            </div>

            {/* Socks */}
            <div className="text-center">
              <div className="relative mb-6 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_141950ea-d755-47d0-b462-4d0e792f15d3/artifacts/fyle24wi_Socks%20in%20Shop.jpg"
                  alt="Socks - Shop Destroyer"
                  className="w-full aspect-square object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-void via-transparent to-transparent" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-2">
                Socks
              </h3>
              <p className="text-cyan-400 font-mono text-sm uppercase tracking-widest mb-2">
                Shop Assistant
              </p>
              <p className="text-neutral-400 text-sm">
                His job is to walk around the shop and destroy everything
              </p>
            </div>

            {/* Dre */}
            <div className="text-center">
              <div className="relative mb-6 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_141950ea-d755-47d0-b462-4d0e792f15d3/artifacts/z5jmiubx_Inspector%20Dre.jpg"
                  alt="Dre - Quality Control"
                  className="w-full aspect-square object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-void via-transparent to-transparent" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-2">
                Dre
              </h3>
              <p className="text-cyan-400 font-mono text-sm uppercase tracking-widest mb-2">
                Quality Control
              </p>
              <p className="text-neutral-400 text-sm">
                He's the sole reason why the shop needs to have a couch
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Shipping Section */}
      <section id="shipping" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-void">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center mx-auto mb-6">
            <Truck className="w-8 h-8 text-cyan-400" />
          </div>
          <span className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-2 block">
            Fast & Reliable
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-white mb-6">
            Shipping
          </h2>
          <p className="text-neutral-300 leading-relaxed max-w-2xl mx-auto">
            Only Skullz LLC uses Shippo for all shipping to deliver fast, reliable orders at the best possible rates. It allows us to choose the best carrier every time and provide clear pricing and real-time tracking—so our clients get dependable delivery without the guesswork.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 text-cyan-400 font-mono text-sm uppercase tracking-widest mt-8 hover:text-cyan-300 transition-colors"
          >
            View Full Shipping Policy <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Find Us In Stores Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-void-paper">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-2 block">
            Retail Partners
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-white mb-6">
            Find Us In Stores
          </h2>
          <p className="text-neutral-400 mb-12">
            You can also find us at a local store
          </p>

          <div className="flex flex-wrap justify-center gap-8">
            <a
              href="https://www.ghoulishmortals.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block p-8 border border-white/10 bg-void-paper hover:border-cyan-400/50 transition-all duration-300"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_141950ea-d755-47d0-b462-4d0e792f15d3/artifacts/ufk8prp8_Image.jpg"
                alt="Ghoulish Mortals"
                className="h-32 w-auto mx-auto"
              />
              <p className="text-cyan-400 font-mono text-sm uppercase tracking-widest mt-4">
                Visit Store →
              </p>
            </a>

            <a
              href="https://aainkz16.wixsite.com/obsidian-ink"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block p-8 border border-white/10 bg-void-paper hover:border-cyan-400/50 transition-all duration-300"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_789e9443-283a-429d-99a1-2af8cb011e09/artifacts/adk3cfny_obsidian%20ink.jpg"
                alt="Obsidian Ink Tattoo Studio - Batavia, IL"
                className="h-32 w-auto mx-auto"
              />
              <p className="text-cyan-400 font-mono text-sm uppercase tracking-widest mt-4">
                Visit Store →
              </p>
            </a>
          </div>
        </div>
      </section>

      {/* Collaborations Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-void-paper">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-2 block">
            Veteran Partnerships
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-white mb-6">
            Collaborations
          </h2>
          <p className="text-neutral-400 mb-12 max-w-2xl mx-auto">
            Supporting the veteran community is important to me. I work with other veteran-owned companies on collaborative projects that reflect integrity, quality, and mutual respect.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Christmas Kings */}
            <a
              href="https://christmaskings.pro"
              target="_blank"
              rel="noopener noreferrer"
              className="p-8 border border-white/10 bg-void hover:border-cyan-400/50 transition-all duration-300"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_141950ea-d755-47d0-b462-4d0e792f15d3/artifacts/j3ix1q6l_Christmas%20kings%20logo.jpeg"
                alt="Christmas Kings"
                className="h-32 w-auto mx-auto"
              />
              <p className="text-cyan-400 font-mono text-sm uppercase tracking-widest mt-4">
                Visit Site →
              </p>
            </a>

            {/* Living Edge Creations */}
            <a
              href="https://www.facebook.com/profile.php?id=61571299047374"
              target="_blank"
              rel="noopener noreferrer"
              className="p-8 border border-white/10 bg-void hover:border-cyan-400/50 transition-all duration-300"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_141950ea-d755-47d0-b462-4d0e792f15d3/artifacts/x2k15lj7_Living%20Edge%20Creations.jpeg"
                alt="Living Edge Creations - Veteran Owned"
                className="h-32 w-auto mx-auto"
              />
              <p className="text-cyan-400 font-mono text-sm uppercase tracking-widest mt-4">
                Visit Facebook →
              </p>
            </a>

            {/* RedLeg Woodshop */}
            <a
              href="https://www.facebook.com/redlegwoodshop"
              target="_blank"
              rel="noopener noreferrer"
              className="p-8 border border-white/10 bg-void hover:border-cyan-400/50 transition-all duration-300"
            >
              <div className="h-32 w-32 mx-auto bg-white rounded-full flex items-center justify-center p-2">
                <img
                  src="https://customer-assets.emergentagent.com/job_141950ea-d755-47d0-b462-4d0e792f15d3/artifacts/rv5ixezu_Redleg%20Logo.png"
                  alt="RedLeg Woodshop"
                  className="h-full w-full object-contain"
                />
              </div>
              <p className="text-cyan-400 font-mono text-sm uppercase tracking-widest mt-4">
                Visit Facebook →
              </p>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
